// ============================================
// Notify — Room Logic (WebRTC + WebSocket)
// ============================================

import SiriWave from 'siriwave';

// Dynamic manifest so "Add to Home Screen" opens this room, not landing
(() => {
  const manifest = {
    name: 'Notify',
    short_name: 'Notify',
    start_url: window.location.pathname,
    display: 'standalone',
    background_color: '#0a0a0f',
    theme_color: '#0a0a0f',
    icons: [
      { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' },
      { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' },
    ],
  };
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = URL.createObjectURL(blob);
  document.head.appendChild(link);
})();

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:111.88.116.96:3478' },
    {
      urls: 'turn:111.88.116.96:3478',
      username: 'notify',
      credential: 'NotifyTurn2026!',
    },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

const MEDIA_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

// --- DOM Elements ---
const localVideo = document.getElementById('localVideo') as HTMLVideoElement;
const remoteVideo = document.getElementById('remoteVideo') as HTMLVideoElement;
const localVideoWrap = document.getElementById('localVideoWrap') as HTMLElement;

const overlayWaiting = document.getElementById('overlayWaiting') as HTMLElement;
const overlayConnecting = document.getElementById('overlayConnecting') as HTMLElement;
const overlayPeerLeft = document.getElementById('overlayPeerLeft') as HTMLElement;
const overlayError = document.getElementById('overlayError') as HTMLElement;
const errorTitle = document.getElementById('errorTitle') as HTMLElement;
const errorMessage = document.getElementById('errorMessage') as HTMLElement;

const inviteLinkEl = document.getElementById('inviteLink') as HTMLElement;
const statusToast = document.getElementById('statusToast') as HTMLElement;
const peerAvatar = document.getElementById('peerAvatar') as HTMLElement;
const peerMicOff = document.getElementById('peerMicOff') as HTMLElement;

const btnMic = document.getElementById('btnMic') as HTMLButtonElement;
const btnCam = document.getElementById('btnCam') as HTMLButtonElement;
const btnScreen = document.getElementById('btnScreen') as HTMLButtonElement;
const btnCopy = document.getElementById('btnCopy') as HTMLButtonElement;
const btnCopyInvite = document.getElementById('btnCopyInvite') as HTMLButtonElement;
const btnEnd = document.getElementById('btnEnd') as HTMLButtonElement;

const iconMicOn = document.getElementById('iconMicOn') as HTMLElement;
const iconMicOff = document.getElementById('iconMicOff') as HTMLElement;
const iconCamOn = document.getElementById('iconCamOn') as HTMLElement;
const iconCamOff = document.getElementById('iconCamOff') as HTMLElement;

const connectionInfo = document.getElementById('connectionInfo') as HTMLElement;
const qualityDot = document.getElementById('qualityDot') as HTMLElement;
const qualityLabel = document.getElementById('qualityLabel') as HTMLElement;
const relayBadge = document.getElementById('relayBadge') as HTMLElement;

const slotsPanel = document.getElementById('slotsPanel') as HTMLElement;
const slotsRow = document.getElementById('slotsRow') as HTMLElement;

const localWaveformHost = document.getElementById('localWaveform') as HTMLElement;
const peerWaveformHost = document.getElementById('peerWaveform') as HTMLElement;

// Adapt remote video object-fit: cover for camera-like ratios, contain for screen/window shares
function updateRemoteFit() {
  const vw = remoteVideo.videoWidth;
  const vh = remoteVideo.videoHeight;
  const cw = remoteVideo.clientWidth;
  const ch = remoteVideo.clientHeight;
  if (!vw || !vh || !cw || !ch) return;
  const videoRatio = vw / vh;
  const containerRatio = cw / ch;
  const diff = Math.abs(videoRatio - containerRatio) / containerRatio;
  remoteVideo.classList.toggle('fit-contain', diff > 0.15);
}
remoteVideo.addEventListener('loadedmetadata', updateRemoteFit);
remoteVideo.addEventListener('resize', updateRemoteFit);
window.addEventListener('resize', updateRemoteFit);

// --- State ---
let localStream: MediaStream | null = null;
let screenStream: MediaStream | null = null;
let pc: RTCPeerConnection | null = null;
let ws: WebSocket | null = null;
let isMicMuted = false;
let isCamOff = false;
let isScreenSharing = false;
let videoDevices: MediaDeviceInfo[] = [];
let currentDeviceIndex = 0;
let isInitiator = false;
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
let currentFacingMode: 'user' | 'environment' = 'user';
let reconnectAttempts = 0;
const MAX_RECONNECT = 3;

let iceRestartAttempts = 0;
const MAX_ICE_RESTART = 3;

let statsInterval: ReturnType<typeof setInterval> | null = null;
let currentQualityTier: 'high' | 'medium' | 'low' = 'high';

// --- Voice waveform (SiriWave Classic driven by audio RMS) ---
interface AudioMeter {
  analyser: AnalyserNode;
  source: MediaStreamAudioSourceNode;
  buf: Uint8Array;
  amp: number;
}

let audioCtx: AudioContext | null = null;
let localMeter: AudioMeter | null = null;
let peerMeter: AudioMeter | null = null;
let localWave: SiriWave | null = null;
let peerWave: SiriWave | null = null;
let peerCamOn = true;
let waveRafId: number | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx) {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = new Ctx();
  }
  return audioCtx;
}

function unlockAudio() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch {}
}

document.addEventListener('pointerdown', unlockAudio, { passive: true });

function createMeter(stream: MediaStream): AudioMeter | null {
  if (stream.getAudioTracks().length === 0) return null;
  try {
    const ctx = getAudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    return { analyser, source, buf: new Uint8Array(analyser.frequencyBinCount), amp: 0.1 };
  } catch {
    return null;
  }
}

function stopMeter(meter: AudioMeter | null) {
  if (!meter) return;
  try { meter.source.disconnect(); } catch {}
}

function computeRms(meter: AudioMeter): number {
  meter.analyser.getByteFrequencyData(meter.buf);
  let sum = 0;
  for (let i = 0; i < meter.buf.length; i++) {
    const v = meter.buf[i] / 255;
    sum += v * v;
  }
  return Math.sqrt(sum / meter.buf.length);
}

const WAVE_OPTIONS_BASE = {
  style: 'ios' as const,
  speed: 0.06,
  amplitude: 0.03,
  frequency: 4,
  autostart: true,
};
const LOCAL_WAVE_OPTIONS = { ...WAVE_OPTIONS_BASE, color: '#000000' };
const PEER_WAVE_OPTIONS = { ...WAVE_OPTIONS_BASE, color: '#ffffff' };

function buildWave(host: HTMLElement, options: typeof LOCAL_WAVE_OPTIONS): SiriWave {
  return new SiriWave({ container: host, ...options });
}

function ensureLocalWave() {
  if (localWave) return;
  localWave = buildWave(localWaveformHost, LOCAL_WAVE_OPTIONS);
}

function ensurePeerWave() {
  if (peerWave) return;
  peerWave = buildWave(peerWaveformHost, PEER_WAVE_OPTIONS);
}

function observeWaveResize(
  host: HTMLElement,
  getWave: () => SiriWave | null,
  setWave: (w: SiriWave) => void,
  options: typeof LOCAL_WAVE_OPTIONS,
) {
  let lastW = 0;
  let lastH = 0;
  let pending = 0;
  const ro = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    const { width, height } = entry.contentRect;
    if (width === lastW && height === lastH) return;
    lastW = width;
    lastH = height;
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => {
      pending = 0;
      const cur = getWave();
      if (!cur || width === 0 || height === 0) return;
      cur.dispose();
      setWave(buildWave(host, options));
    });
  });
  ro.observe(host);
}

observeWaveResize(localWaveformHost, () => localWave, (w) => { localWave = w; }, LOCAL_WAVE_OPTIONS);
observeWaveResize(peerWaveformHost, () => peerWave, (w) => { peerWave = w; }, PEER_WAVE_OPTIONS);

function startLocalMeter() {
  if (localMeter || !localStream) return;
  localMeter = createMeter(localStream);
  if (localMeter) startWaveLoop();
}

function startPeerMeter() {
  if (peerMeter) return;
  const stream = remoteVideo.srcObject as MediaStream | null;
  if (!stream) return;
  peerMeter = createMeter(stream);
  if (peerMeter) startWaveLoop();
}

function startWaveLoop() {
  if (waveRafId !== null) return;
  const tick = () => {
    updateWave(localMeter, localWave, isCamOff);
    updateWave(peerMeter, peerWave, !peerCamOn);
    waveRafId = requestAnimationFrame(tick);
  };
  waveRafId = requestAnimationFrame(tick);
}

function updateWave(meter: AudioMeter | null, wave: SiriWave | null, active: boolean) {
  if (!meter || !wave) return;
  if (!active) return;
  const rms = computeRms(meter);
  // Idle almost flat (0.03); normal speech ~0.15; loud capped at 0.3
  const target = Math.min(0.3, 0.03 + Math.max(0, rms - 0.01) * 1.2);
  const alpha = target > meter.amp ? 0.2 : 0.04;
  meter.amp += (target - meter.amp) * alpha;
  wave.setAmplitude(meter.amp);
  wave.setSpeed(0.05 + Math.min(0.08, rms * 0.6));
}

// Extract roomId from URL
const roomId = window.location.pathname.slice(1);
if (!roomId) {
  window.location.href = '/';
}

// Invite code (may be null for guests joining via link)
const inviteCode = localStorage.getItem('notify-invite-code');

// Device identifier: use invite code if available (same across Safari & PWA),
// otherwise generate a session-unique ID (guest won't open same room in 2 windows)
const myDeviceId = inviteCode || ('guest-' + Math.random().toString(36).slice(2, 10));

function getHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (inviteCode) h['X-Invite-Code'] = inviteCode;
  return h;
}

// Set invite link
const inviteUrl = `${window.location.origin}/${roomId}`;
inviteLinkEl.textContent = inviteUrl;

// --- Overlay Management ---
function showOverlay(overlay: HTMLElement) {
  [overlayWaiting, overlayConnecting, overlayPeerLeft, overlayError].forEach(o => {
    o.classList.add('hidden');
  });
  overlay.classList.remove('hidden');
}

function hideAllOverlays() {
  [overlayWaiting, overlayConnecting, overlayPeerLeft, overlayError].forEach(o => {
    o.classList.add('hidden');
  });
}

function showError(title: string, message: string) {
  errorTitle.textContent = title;
  errorMessage.textContent = message;
  errorMessage.style.display = message ? '' : 'none';
  showOverlay(overlayError);
}

// --- Toast ---
let toastTimeout: ReturnType<typeof setTimeout> | null = null;

function showToast(text: string, duration = 3000) {
  if (toastTimeout) clearTimeout(toastTimeout);
  statusToast.textContent = text;
  statusToast.classList.add('visible');
  toastTimeout = setTimeout(() => {
    statusToast.classList.remove('visible');
  }, duration);
}

// --- Slots ---
interface Slot {
  slot_number: number;
  room_id: string;
}

let slots: Slot[] = [];
let longPressTimer: ReturnType<typeof setTimeout> | null = null;
const LONG_PRESS_MS = 800;

async function loadSlots() {
  if (!inviteCode) return;
  try {
    const res = await fetch('/api/slots', { headers: getHeaders() });
    if (!res.ok) return;
    const data = await res.json();
    slots = data.slots || [];
    renderSlots();
    slotsRow.classList.remove('hidden');
  } catch {}
}

function renderSlots() {
  const btns = slotsPanel.querySelectorAll('.slot-btn');
  btns.forEach(btn => {
    const num = parseInt((btn as HTMLElement).dataset.slot || '0', 10);
    const slot = slots.find(s => s.slot_number === num);
    const el = btn as HTMLElement;
    if (slot) {
      el.classList.remove('slot-empty');
      el.classList.add('slot-bound');
      // Highlight if this is the current room
      el.classList.toggle('slot-current', slot.room_id === roomId);
    } else {
      el.classList.add('slot-empty');
      el.classList.remove('slot-bound', 'slot-current');
    }
  });
}

async function bindSlot(slotNum: number) {
  if (!inviteCode) return;
  try {
    const res = await fetch(`/api/slots/${slotNum}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ roomId }),
    });
    if (res.ok) {
      showToast(`Комната сохранена в слот ${slotNum}`);
      await loadSlots();
    }
  } catch {}
}

async function clearSlot(slotNum: number) {
  if (!inviteCode) return;
  try {
    const res = await fetch(`/api/slots/${slotNum}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (res.ok) {
      showToast(`Слот ${slotNum} очищен`);
      await loadSlots();
    }
  } catch {}
}

// Slot interaction: short tap = navigate, long press = bind/rebind/clear
function setupSlotEvents() {
  slotsPanel.addEventListener('pointerdown', (e) => {
    const btn = (e.target as HTMLElement).closest('.slot-btn') as HTMLElement | null;
    if (!btn) return;
    const num = parseInt(btn.dataset.slot || '0', 10);

    longPressTimer = setTimeout(() => {
      longPressTimer = null;
      const slot = slots.find(s => s.slot_number === num);
      if (slot) {
        // Bound slot: ask rebind or clear
        if (slot.room_id === roomId) {
          clearSlot(num);
        } else {
          bindSlot(num);
        }
      } else {
        // Empty slot: bind
        bindSlot(num);
      }
    }, LONG_PRESS_MS);
  });

  slotsPanel.addEventListener('pointerup', (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
      // Short tap — navigate to bound room
      const btn = (e.target as HTMLElement).closest('.slot-btn') as HTMLElement | null;
      if (!btn) return;
      const num = parseInt(btn.dataset.slot || '0', 10);
      const slot = slots.find(s => s.slot_number === num);
      if (slot && slot.room_id !== roomId) {
        window.location.href = `/${slot.room_id}`;
      }
    }
  });

  slotsPanel.addEventListener('pointercancel', () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  });

  // Prevent context menu on long press (mobile)
  slotsPanel.addEventListener('contextmenu', (e) => e.preventDefault());
}

setupSlotEvents();

// --- Push subscription for this room ---
async function subscribePushForRoom() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    // Register SW if not already
    await navigator.serviceWorker.register('/sw.js');
    const reg = await navigator.serviceWorker.ready;
    const res = await fetch('/api/push/vapid-key');
    const { key } = await res.json();
    if (!key) return;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
    }

    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomId, subscription: sub }),
    });
  } catch {}
}

// --- Media ---
async function getLocalMedia(): Promise<boolean> {
  try {
    localStream = await navigator.mediaDevices.getUserMedia(MEDIA_CONSTRAINTS);
    localVideo.srcObject = localStream;
    await loadVideoDevices();
    renderCameraDots();
    return true;
  } catch (err: unknown) {
    // Try audio-only
    try {
      localStream = await navigator.mediaDevices.getUserMedia({ audio: MEDIA_CONSTRAINTS.audio });
      localVideo.srcObject = localStream;
      isCamOff = true;
      updateCamButton();
      updateLocalWaveVisibility();
      return true;
    } catch {
      showError(
        'Камера недоступна',
        'Разрешите доступ к камере и микрофону для участия в звонке.'
      );
      return false;
    }
  }
}

// Filter desktop cameras: keep only real physical cameras
function filterDesktopCameras(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter(d => {
    const label = d.label.toLowerCase();
    // Continuity Camera (phone near computer)
    if (label.includes('iphone') || label.includes('ipad') || label.includes('android')) return false;
    // Virtual / system cameras
    if (label.includes('virtual') || label.includes('obs') || label.includes('snap camera') || label.includes('manycam') || label.includes('camo') || label.includes('desk view')) return false;
    return true;
  });
}

async function loadVideoDevices() {
  const devices = await navigator.mediaDevices.enumerateDevices();
  if (isMobile) {
    // Mobile uses facingMode toggle, no device list needed
    videoDevices = [];
  } else {
    const allVideo = devices.filter(d => d.kind === 'videoinput');
    videoDevices = filterDesktopCameras(allVideo);
    console.log('[cameras]', videoDevices.map((d, i) => `${i}: "${d.label}" (${d.deviceId.slice(0, 8)}…)`));
  }
  const currentTrack = localStream?.getVideoTracks()[0];
  if (currentTrack && videoDevices.length > 0) {
    const settings = currentTrack.getSettings();
    const idx = videoDevices.findIndex(d => d.deviceId === settings.deviceId);
    if (idx >= 0) currentDeviceIndex = idx;
  }
}

// --- WebSocket ---
function connectWebSocket() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  ws = new WebSocket(`${protocol}//${window.location.host}`);

  ws.onopen = () => {
    reconnectAttempts = 0;
    ws!.send(JSON.stringify({ type: 'join', roomId, deviceId: myDeviceId }));
  };

  ws.onmessage = async (event) => {
    const message = JSON.parse(event.data);
    await handleSignalingMessage(message);
  };

  ws.onclose = () => {
    if (reconnectAttempts < MAX_RECONNECT) {
      reconnectAttempts++;
      const delay = Math.pow(2, reconnectAttempts) * 1000;
      setTimeout(connectWebSocket, delay);
    }
  };

  ws.onerror = () => {
    // onclose will handle reconnect
  };
}

function sendMessage(message: Record<string, unknown>) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(message));
  }
}

// --- Stats Monitoring ---
function startStatsMonitor() {
  if (statsInterval) clearInterval(statsInterval);
  statsInterval = setInterval(async () => {
    if (!pc) return;
    const stats = await pc.getStats();
    let rtt = 0;
    let packetLoss = 0;
    let isRelay = false;

    stats.forEach((report: Record<string, unknown>) => {
      if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
        rtt = (report.currentRoundTripTime as number) * 1000;

        const localCandidate = stats.get(report.localCandidateId as string);
        if (localCandidate && localCandidate.candidateType === 'relay') {
          isRelay = true;
        }
      }

      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        const received = report.packetsReceived as number;
        const lost = report.packetsLost as number;
        const total = received + lost;
        if (total > 0) {
          packetLoss = (lost / total) * 100;
        }
      }
    });

    updateQualityIndicator(rtt, packetLoss);
    updateRelayIndicator(isRelay);
    adjustVideoQuality(rtt, packetLoss);
  }, 2000);
}

function stopStatsMonitor() {
  if (statsInterval) {
    clearInterval(statsInterval);
    statsInterval = null;
  }
}

function updateQualityIndicator(rtt: number, packetLoss: number) {
  let quality: 'good' | 'fair' | 'poor';
  if (rtt < 100 && packetLoss < 1) {
    quality = 'good';
  } else if (rtt < 300 && packetLoss < 5) {
    quality = 'fair';
  } else {
    quality = 'poor';
  }

  qualityDot.className = `quality-dot quality-${quality}`;
  qualityLabel.textContent = rtt > 0 ? `${Math.round(rtt)} мс` : '';
}

function updateRelayIndicator(isRelay: boolean) {
  relayBadge.classList.toggle('visible', isRelay);
}

// --- Adaptive Bitrate ---
async function adjustVideoQuality(rtt: number, packetLoss: number) {
  if (!pc || isScreenSharing) return;

  let targetTier: 'high' | 'medium' | 'low';
  if (rtt < 150 && packetLoss < 2) {
    targetTier = 'high';
  } else if (rtt < 400 && packetLoss < 8) {
    targetTier = 'medium';
  } else {
    targetTier = 'low';
  }

  if (targetTier === currentQualityTier) return;
  currentQualityTier = targetTier;

  const sender = pc.getSenders().find(s => s.track?.kind === 'video');
  if (!sender) return;

  const params = sender.getParameters();
  if (!params.encodings || params.encodings.length === 0) {
    params.encodings = [{}];
  }

  switch (targetTier) {
    case 'high':
      params.encodings[0].maxBitrate = 2_500_000;
      params.encodings[0].scaleResolutionDownBy = 1;
      break;
    case 'medium':
      params.encodings[0].maxBitrate = 1_000_000;
      params.encodings[0].scaleResolutionDownBy = 1.5;
      break;
    case 'low':
      params.encodings[0].maxBitrate = 400_000;
      params.encodings[0].scaleResolutionDownBy = 2;
      break;
  }

  await sender.setParameters(params);
}

// --- WebRTC ---
function createPeerConnection() {
  pc = new RTCPeerConnection(ICE_SERVERS);

  // Add local tracks
  if (localStream) {
    localStream.getTracks().forEach(track => {
      pc!.addTrack(track, localStream!);
    });
  }

  // ICE candidates
  pc.onicecandidate = (event) => {
    if (event.candidate) {
      sendMessage({
        type: 'candidate',
        candidate: event.candidate.toJSON(),
        roomId,
      });
    }
  };

  // Remote stream
  pc.ontrack = (event) => {
    if (event.streams[0]) {
      remoteVideo.srcObject = event.streams[0];
      // If peer already signalled cam-off, spin up meter now that the stream is here
      if (!peerCamOn) startPeerMeter();
    }
  };

  pc.onconnectionstatechange = () => {
    if (!pc) return;
    switch (pc.connectionState) {
      case 'connected':
        iceRestartAttempts = 0;
        hideAllOverlays();
        showToast('Соединение установлено');
        connectionInfo.classList.add('visible');
        startStatsMonitor();
        // Subscribe to push for this room (so we get notified next time)
        subscribePushForRoom();
        break;
      case 'disconnected':
        showToast('Соединение прервано, переподключение…');
        setTimeout(async () => {
          if (pc && pc.connectionState === 'disconnected') {
            iceRestartAttempts++;
            if (iceRestartAttempts <= MAX_ICE_RESTART) {
              try {
                const offer = await pc.createOffer({ iceRestart: true });
                await pc.setLocalDescription(offer);
                sendMessage({ type: 'offer', sdp: pc.localDescription, roomId });
              } catch {
                // PC may have been closed
              }
            }
          }
        }, 3000);
        break;
      case 'failed':
        if (iceRestartAttempts <= MAX_ICE_RESTART) {
          iceRestartAttempts++;
          (async () => {
            try {
              const offer = await pc!.createOffer({ iceRestart: true });
              await pc!.setLocalDescription(offer);
              sendMessage({ type: 'offer', sdp: pc!.localDescription, roomId });
              showToast('Переподключение…');
            } catch {
              showError(
                'Не удалось подключиться',
                'Не удалось установить соединение. Возможно, один из участников за строгим фаерволом.'
              );
            }
          })();
        } else {
          showError(
            'Не удалось подключиться',
            'Не удалось установить соединение. Возможно, один из участников за строгим фаерволом.'
          );
        }
        break;
    }
  };

  return pc;
}

async function handleSignalingMessage(message: Record<string, unknown>) {
  const type = message.type as string;

  switch (type) {
    case 'ready': {
      showOverlay(overlayConnecting);
      showToast('Собеседник подключился');
      // Re-acquire media if needed (after resetToWaiting stopped it)
      if (!localStream) {
        await getLocalMedia();
      }
      createPeerConnection();

      isInitiator = message.initiator as boolean;
      if (isInitiator) {
        const offer = await pc!.createOffer();
        await pc!.setLocalDescription(offer);
        sendMessage({ type: 'offer', sdp: pc!.localDescription, roomId });
      }
      // Let the new peer know our current camera state
      sendMessage({ type: 'cam-state', on: !isCamOff, roomId });
      break;
    }

    case 'cam-state': {
      peerCamOn = message.on as boolean;
      updatePeerWaveVisibility();
      break;
    }

    case 'offer': {
      if (!pc) createPeerConnection();
      await pc!.setRemoteDescription(new RTCSessionDescription(message.sdp as RTCSessionDescriptionInit));
      const answer = await pc!.createAnswer();
      await pc!.setLocalDescription(answer);
      sendMessage({ type: 'answer', sdp: pc!.localDescription, roomId });
      break;
    }

    case 'answer': {
      if (pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(message.sdp as RTCSessionDescriptionInit));
      }
      break;
    }

    case 'candidate': {
      if (pc) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(message.candidate as RTCIceCandidateInit));
        } catch {
          // Ignore ICE candidate errors
        }
      }
      break;
    }

    case 'not-found': {
      showError('Комната не найдена', '');
      break;
    }

    case 'full': {
      showError('Комната занята', 'В этой комнате уже два участника.');
      break;
    }

    case 'leave': {
      cleanupPeerConnection();
      showOverlay(overlayPeerLeft);
      showToast('Собеседник вышел');
      break;
    }
  }
}

function cleanupPeerConnection() {
  stopStatsMonitor();
  connectionInfo.classList.remove('visible');
  if (pc) {
    pc.close();
    pc = null;
  }
  remoteVideo.srcObject = null;
  peerAvatar.classList.remove('visible');
  peerMicOff.classList.remove('visible');
  stopMeter(peerMeter);
  peerMeter = null;
  peerCamOn = true;
  iceRestartAttempts = 0;
  currentQualityTier = 'high';
}

// --- Controls ---
function updateMicButton() {
  btnMic.classList.toggle('muted', isMicMuted);
  iconMicOn.classList.toggle('hidden', isMicMuted);
  iconMicOff.classList.toggle('hidden', !isMicMuted);
  btnMic.setAttribute('data-tooltip', isMicMuted ? 'Вкл. микрофон' : 'Выкл. микрофон');
}

function updateCamButton() {
  btnCam.classList.toggle('muted', isCamOff);
  iconCamOn.classList.toggle('hidden', isCamOff);
  iconCamOff.classList.toggle('hidden', !isCamOff);
  btnCam.setAttribute('data-tooltip', isCamOff ? 'Вкл. камеру' : 'Выкл. камеру');
}

btnMic.addEventListener('click', () => {
  if (!localStream) return;
  isMicMuted = !isMicMuted;
  localStream.getAudioTracks().forEach(track => {
    track.enabled = !isMicMuted;
  });
  updateMicButton();
});

btnCam.addEventListener('click', () => {
  if (!localStream) return;
  isCamOff = !isCamOff;
  localStream.getVideoTracks().forEach(track => {
    track.enabled = !isCamOff;
  });
  updateCamButton();
  updateLocalWaveVisibility();
  sendMessage({ type: 'cam-state', on: !isCamOff, roomId });
});

function updateLocalWaveVisibility() {
  localVideoWrap.classList.toggle('cam-off', isCamOff);
  if (isCamOff) {
    ensureLocalWave();
    startLocalMeter();
  }
}

function updatePeerWaveVisibility() {
  peerAvatar.classList.toggle('visible', !peerCamOn);
  if (!peerCamOn) {
    ensurePeerWave();
    startPeerMeter();
  }
}

btnScreen.addEventListener('click', async () => {
  if (!pc) return;

  if (isScreenSharing) {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      screenStream = null;
    }
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(videoTrack);
        }
        localVideo.srcObject = localStream;
      }
    }
    isScreenSharing = false;
    btnScreen.classList.remove('active');
    btnScreen.setAttribute('data-tooltip', 'Показать экран');
    return;
  }

  try {
    screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    const screenTrack = screenStream.getVideoTracks()[0];

    const sender = pc.getSenders().find(s => s.track?.kind === 'video');
    if (sender) {
      await sender.replaceTrack(screenTrack);
    }

    localVideo.srcObject = screenStream;
    isScreenSharing = true;
    btnScreen.classList.add('active');
    btnScreen.setAttribute('data-tooltip', 'Остановить показ');

    screenTrack.onended = () => {
      btnScreen.click();
    };
  } catch {
    // User cancelled screen share
  }
});

// Camera dots indicator
const cameraDots = document.getElementById('cameraDots') as HTMLElement;

function renderCameraDots() {
  cameraDots.innerHTML = '';
  // No dots on mobile — simple front/back toggle
  if (isMobile || videoDevices.length <= 1) {
    cameraDots.classList.add('hidden');
    return;
  }
  cameraDots.classList.remove('hidden');
  videoDevices.forEach((_, i) => {
    const dot = document.createElement('span');
    dot.className = 'camera-dot' + (i === currentDeviceIndex ? ' active' : '');
    cameraDots.appendChild(dot);
  });
}

// Clean camera label: remove USB vendor:product IDs like "(0400:0000)"
function cleanDeviceLabel(label: string): string {
  return label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)/gi, '').trim();
}

// Camera name label
const cameraLabel = document.getElementById('cameraLabel') as HTMLElement;
let cameraLabelTimer: ReturnType<typeof setTimeout> | null = null;

function showCameraLabel(name: string) {
  cameraLabel.textContent = name;
  cameraLabel.classList.add('visible');
  if (cameraLabelTimer) clearTimeout(cameraLabelTimer);
  cameraLabelTimer = setTimeout(() => cameraLabel.classList.remove('visible'), 2000);
}

// Tap on local video to switch camera
let switchingCamera = false;
localVideoWrap.addEventListener('click', async () => {
  if (switchingCamera) return;
  if (!localStream || !localStream.getVideoTracks().length) return;

  switchingCamera = true;

  try {
    if (isMobile) {
      // Mobile: simple front ↔ back toggle via facingMode
      currentFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: currentFacingMode } },
        audio: false,
      });

      localStream.getVideoTracks().forEach(track => track.stop());
      const newTrack = newStream.getVideoTracks()[0];
      const oldTrack = localStream.getVideoTracks()[0];
      if (oldTrack) localStream.removeTrack(oldTrack);
      localStream.addTrack(newTrack);
      localVideo.srcObject = localStream;

      if (pc) {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(newTrack);
      }
    } else {
      // Desktop: cycle through all cameras by deviceId
      if (videoDevices.length <= 1) { switchingCamera = false; return; }

      const prevIndex = currentDeviceIndex;
      for (let attempt = 0; attempt < videoDevices.length - 1; attempt++) {
        const nextIndex = (prevIndex + attempt + 1) % videoDevices.length;
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: videoDevices[nextIndex].deviceId } },
            audio: false,
          });

          localStream.getVideoTracks().forEach(track => track.stop());
          const newTrack = newStream.getVideoTracks()[0];
          const oldTrack = localStream.getVideoTracks()[0];
          if (oldTrack) localStream.removeTrack(oldTrack);
          localStream.addTrack(newTrack);
          localVideo.srcObject = localStream;

          currentDeviceIndex = nextIndex;
          renderCameraDots();
          showCameraLabel(cleanDeviceLabel(videoDevices[nextIndex].label) || `Камера ${nextIndex + 1}`);

          if (pc) {
            const sender = pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) await sender.replaceTrack(newTrack);
          }
          break;
        } catch {
          // This camera failed, try next
        }
      }
    }
  } catch {
    // Failed to switch, revert facingMode on mobile
    if (isMobile) {
      currentFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
    }
  }

  switchingCamera = false;
});

btnCopy.addEventListener('click', () => copyInviteLink());
btnCopyInvite.addEventListener('click', () => copyInviteLink());

function copyInviteLink() {
  navigator.clipboard.writeText(inviteUrl).then(() => {
    showToast('Ссылка скопирована!');
  });
}

btnEnd.addEventListener('click', () => {
  sendMessage({ type: 'leave', roomId });
  cleanup();
  window.location.href = '/';
});

const btnWaitAgain = document.getElementById('btnWaitAgain') as HTMLButtonElement;
btnWaitAgain.addEventListener('click', () => {
  resetToWaiting();
});

function resetToWaiting() {
  cleanupPeerConnection();
  // Stop camera/mic to save battery while waiting
  if (localStream) {
    localStream.getTracks().forEach(track => track.stop());
    localStream = null;
  }
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
    screenStream = null;
  }
  isScreenSharing = false;
  isMicMuted = false;
  isCamOff = false;

  stopMeter(localMeter);
  localMeter = null;
  localVideoWrap.classList.remove('cam-off');

  showOverlay(overlayWaiting);

  // Close WS completely to avoid ghost connections, then reconnect fresh
  if (ws) {
    ws.onclose = null; // prevent auto-reconnect handler
    ws.close();
    ws = null;
  }
  setTimeout(() => {
    reconnectAttempts = 0;
    connectWebSocket();
  }, 500);
}

function cleanup() {
  cleanupPeerConnection();
  if (localStream) {
    localStream.getTracks().forEach(track => track.stop());
    localStream = null;
  }
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
    screenStream = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  sendMessage({ type: 'leave', roomId });
  cleanup();
});

// --- Room Info Modal ---
const roomInfoModal = document.getElementById('roomInfoModal') as HTMLElement;
const btnRoomInfo = document.getElementById('btnRoomInfo') as HTMLButtonElement;
const btnCloseRoomInfo = document.getElementById('btnCloseRoomInfo') as HTMLButtonElement;

btnRoomInfo.addEventListener('click', () => {
  roomInfoModal.classList.remove('hidden');
});

btnCloseRoomInfo.addEventListener('click', () => {
  roomInfoModal.classList.add('hidden');
});

roomInfoModal.addEventListener('click', (e) => {
  if (e.target === roomInfoModal) {
    roomInfoModal.classList.add('hidden');
  }
});

// Tab switching
roomInfoModal.querySelectorAll('.modal-tab').forEach(tab => {
  tab.addEventListener('click', () => {
    const tabName = (tab as HTMLElement).dataset.tab;
    roomInfoModal.querySelectorAll('.modal-tab').forEach(t => t.classList.remove('active'));
    roomInfoModal.querySelectorAll('.modal-tab-content').forEach(c => c.classList.remove('active'));
    tab.classList.add('active');
    const content = document.getElementById(tabName === 'slots' ? 'tabSlots' : 'tabApp');
    content?.classList.add('active');
  });
});

// --- Initialize ---
async function init() {
  // Check if room exists before requesting camera
  try {
    const res = await fetch(`/api/rooms/${roomId}`);
    if (!res.ok) {
      showError('Комната не найдена', '');
      return;
    }
  } catch {
    showError('Комната не найдена', '');
    return;
  }

  const hasMedia = await getLocalMedia();
  if (!hasMedia) return;

  showOverlay(overlayWaiting);
  connectWebSocket();

  // Subscribe to push for this room (so we get notified when someone joins)
  subscribePushForRoom();

  // Load slots if user has invite code
  if (inviteCode) {
    loadSlots();
  }
}

init();
